import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { RootState } from "../store";
import { RedesignCases } from "../../types/RedesignCases";
import api from "../../utils/api";

interface RedesignState {
  allRedesigns: Record<string, RedesignCases[]>; // changed from array to object
  caseRedesigns: RedesignCases[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: RedesignState = {
  allRedesigns: {},
  caseRedesigns: [],
  totalCount: 0,
  totalPages: 0,
  currentPage: 1,
  isLoading: false,
  error: null,
};

interface GetRedesignsParams {
  page?: number;
  search?: string;
  status?: string;
}

export const addRedesign = createAsyncThunk(
  "redesignCases/addRedesign",
  async (
    {
      case_id,
      user_id,
      priority,
      status,
      category,
      adminUploads,
      clientUploads,
      note,
      sendBy
    }: {
      case_id: string;
      user_id : number
      priority: string;
      status: string;
      category: string;
      adminUploads: string;
      clientUploads: string;
      note: string;
      sendBy:string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/auth/admin/redesign/add`,
        {
          case_id,
          user_id,
          priority,
          status,
          category,
          adminUploads,
          clientUploads,
          note,
          sendBy,
        }
      );
      return res?.data?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.message);
    }
  }
);

export const fetchRedesignByCaseId = createAsyncThunk(
  "redesignCases/fetchRedesignByCaseId",
  async (case_id: string, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/auth/admin/redesign/${case_id}`
      );
      return res?.data?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.message);
    }
  }
);

export const fetchAllRedesigns = createAsyncThunk(
  "redesignCases/fetchAllRedesigns",
  async (params: GetRedesignsParams, { rejectWithValue, getState }) => {
    try {
      const { page = 1, search = "", status = "" } = params;
      const response = await api.get(
        `/auth/admin/all-redesigns`,
        { params: { page, search, status } }
      );

      const newData = response.data.data; // { data, totalCount, ... }
      const currentState = (getState() as RootState).redesignCases.allRedesigns;

      const isSame = isEqual(currentState, newData?.data || {});

      return {
        ...newData,
        __skipUpdate: isSame,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch redesigns"
      );
    }
  }
);

export const updateRedesignStatus = createAsyncThunk(
  "redesignCases/updateRedesignStatus",
  async (
    {
      case_id,
      status,
      redesign_attempt,
    }: { case_id: string; status: string; redesign_attempt: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/api/localUploader/update-redesign-status`,
        {
          case_id,
          status,
          redesign_attempt,
        }
      );
      return res.data.data;
    } catch (error: any) {
      console.error("Error updating redesign status:", error);
      return rejectWithValue(
        error?.response?.data?.error || "Failed to update redesign status"
      );
    }
  }
);

const redesignCasesSlice = createSlice({
  name: "redesignCases",
  initialState,
  reducers: {
    clearRedesign: (state) => {
      state.allRedesigns = {};
      state.caseRedesigns = []; 
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addRedesign.pending, (state) => {
        (state.isLoading = true), (state.error = null);
      })
      .addCase(addRedesign.fulfilled, (state, action) => {
        (state.isLoading = false), (state.caseRedesigns = action.payload);
      })
      .addCase(addRedesign.rejected, (state, action) => {
        (state.isLoading = false), (state.error = action.payload as string);
      })

      .addCase(fetchRedesignByCaseId.pending, (state) => {
        (state.isLoading = true), (state.error = null);
      })
      .addCase(fetchRedesignByCaseId.fulfilled, (state, action) => {
        (state.isLoading = false), (state.caseRedesigns = action.payload)
      })
      .addCase(fetchRedesignByCaseId.rejected, (state, action) => {
        (state.isLoading = false), (state.error = action.payload as string);
      })

      // --- fetchAllRedesigns ---
      .addCase(fetchAllRedesigns.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRedesigns.fulfilled, (state, action) => {
        state.isLoading = false;
        state.totalCount = action.payload.totalCount || 0;
        state.totalPages = action.payload.totalPages || 0;
        state.currentPage = action.payload.currentPage || 1;
        if (!action.payload.__skipUpdate) {
          state.allRedesigns = action.payload.data || {};
        }
      })
      .addCase(fetchAllRedesigns.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearRedesign } = redesignCasesSlice.actions;

export default redesignCasesSlice.reducer;
